 import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { GoogleLogo, EnvelopeSimple, LockSimple, UserCircle } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function Login() {
  const { loginEmail, signupEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login"); // login | signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("customer");
  const [loading, setLoading] = useState(false);

  const redirectTo = location.state?.from?.pathname || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await loginEmail(email, password);
        toast.success("Welcome back!");
      } else {
        await signupEmail(email, password, name, role);
        toast.success("Account created");
      }
      navigate(redirectTo, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    }
    setLoading(false);
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    // Break out of iframe if the app is embedded (preview mode) — Google OAuth
    // refuses to run inside third-party iframes, so we must navigate the top window.
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = authUrl;
        return;
      }
    } catch {
      // Cross-origin iframe — fall through
    }
    window.location.href = authUrl;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-24 bg-[#F9F7F3]" data-testid="auth-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-6">
          <img
            src="/afripantry-logo.png"
            alt="AfriPantry"
            className="h-14 md:h-16 w-auto"
          />
        </Link>

        <div className="card-surface p-6 md:p-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-2">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-sm text-[#5C5C54] mb-6">
            {mode === "login"
              ? "Sign in to track orders and reorder favourites."
              : "Join AfriPantry to shop from trusted UK vendors."}
          </p>

          <button
            onClick={handleGoogle}
            data-testid="google-login-btn"
            className="w-full btn-secondary mb-4 gap-2"
          >
            <GoogleLogo size={18} weight="bold" /> Continue with Google
          </button>

          <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-center text-[#5C5C54] mb-5 flex items-center justify-center gap-1.5">
            <LockSimple size={11} weight="fill" className="text-[#2B5329]" />
            Secure sign-in · AfriPantry
          </p>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-[#E5E2D9]" />
            <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#5C5C54]">or</span>
            <div className="flex-1 h-px bg-[#E5E2D9]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === "signup" && (
              <>
                <div className="relative">
                  <UserCircle size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C5C54]" />
                  <input
                    data-testid="signup-name-input"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    className="input-field pl-11"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {["customer", "vendor"].map((r) => (
                    <button
                      type="button"
                      key={r}
                      data-testid={`role-${r}`}
                      onClick={() => setRole(r)}
                      className={`rounded-xl border-2 px-4 py-2.5 text-sm font-semibold capitalize transition-all ${role === r ? "border-[#A94438] bg-[#A94438]/5 text-[#A94438]" : "border-[#E5E2D9] text-[#5C5C54] hover:border-[#D1CEC5]"}`}
                    >
                      I'm a {r}
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="relative">
              <EnvelopeSimple size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C5C54]" />
              <input
                data-testid="auth-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="input-field pl-11"
              />
            </div>

            <div className="relative">
              <LockSimple size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C5C54]" />
              <input
                data-testid="auth-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="input-field pl-11"
              />
            </div>

            <button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50"
            >
              {loading ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="text-xs text-center text-[#5C5C54] mt-5">
            {mode === "login" ? (
              <>
                New to AfriPantry?{" "}
                <button onClick={() => setMode("signup")} data-testid="toggle-signup" className="text-[#A94438] font-bold hover:underline">
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button onClick={() => setMode("login")} data-testid="toggle-login" className="text-[#A94438] font-bold hover:underline">
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>

        <p className="text-[10px] text-center text-[#5C5C54]/80 mt-6 max-w-xs mx-auto">
          Secure sign-in · AfriPantry. By continuing you agree to our Terms & Privacy. GDPR-compliant · encrypted payments.
        </p>
      </div>
    </div>
  );
}
